<script setup lang="ts">
import { Card, NoteComponent } from '@metapic-apps/shared'
import type { FormInstance, FormRules } from 'element-plus'
import { inject, onMounted, reactive, ref, watch } from 'vue'

import { defaultStyle } from '@/components/ReturnAdForm/ReturnAdForm.helpers'
import ReturnAdFormCalendar from '@/components/ReturnAdForm/ReturnAdFormCalendar.vue'
import ReturnAdFormCustomization from '@/components/ReturnAdForm/ReturnAdFormCustomization.vue'
import ReturnAdFormTarget from '@/components/ReturnAdForm/ReturnAdFormTarget/ReturnAdFormTarget.vue'
import { normalizeReturnAdFormToPreview } from '@/lib/adapters/return-ad.adapter'
import { validateUrl } from '@/lib/helpers'
import { previewDataProvideFlag } from '@/lib/hooks/provider.hooks'
import type { ReturnAdFormType } from '@/lib/types/return-ad.types'
import { BoxComponent, ButtonComponent, FlexComponent } from '@/ui-library'

const props = withDefaults(
  defineProps<{ returnAd?: ReturnAdFormType; defaultAdvertiserGroupId: number; isLoading: boolean; isEditMode?: boolean }>(),
  { isEditMode: false, returnAd: undefined },
)
const emit = defineEmits(['handleSubmit', 'handleBack'])
const previewData = inject(previewDataProvideFlag)

const formData = reactive<ReturnAdFormType>(
  props.returnAd || {
    internalName: '',
    advertiserList: [],
    advertiserGroup: props.defaultAdvertiserGroupId,
    returnLink: '',
    categories: [],
    isAllAdvertisersSelected: false,
    styles: {
      text: '',
      backgroundType: defaultStyle.backgroundType,
      backgroundColor: defaultStyle.backgroundColor,
      textColor: defaultStyle.textColor,
      buttonBackgroundColor: defaultStyle.buttonBackgroundColor,
      buttonTextColor: defaultStyle.buttonTextColor,
      textPosition: defaultStyle.textPosition,
    },
    startsAt: '',
    endsAt: '',
    advertiser: undefined,
    tempImage: undefined,
    hasStartsNow: true,
    hasOpenEnd: true,
    clickLimit: 0,
    viewLimit: 0,
  },
)
const ruleFormRef = ref<FormInstance>()
const rules = reactive<FormRules>({
  internalName: [{ required: true, message: 'This field is required', trigger: 'change' }],
  advertiserGroup: [{ required: true, message: 'This field is required', trigger: 'submit' }],
  returnLink: [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_, value: string, callback) => {
        if (!validateUrl(value)) {
          callback(new Error('Invalid URL'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  advertiserList: [
    {
      validator: (_, value: string, callback) => {
        if (!formData.isAllAdvertisersSelected && !formData.categories.length && !formData.advertiserList.length) {
          callback(new Error('Choose at least one advertiser or category'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  'styles.textColor': [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_, value: string, callback) => {
        const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/
        if (!hexColorRegex.test(value)) {
          callback(new Error('Enter a valid color value'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  'styles.backgroundColor': [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_, value: string, callback) => {
        const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/
        if (!hexColorRegex.test(value)) {
          callback(new Error('Enter a valid color value'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  'styles.buttonBackgroundColor': [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_, value: string, callback) => {
        const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/
        if (!hexColorRegex.test(value)) {
          callback(new Error('Enter a valid color value'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  'styles.buttonTextColor': [
    { required: true, message: 'This field is required', trigger: 'change' },
    {
      validator: (_, value: string, callback) => {
        const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3}|[A-Fa-f0-9]{8})$/
        if (!hexColorRegex.test(value)) {
          callback(new Error('Enter a valid color value'))
        } else {
          callback()
        }
      },
      trigger: 'change',
    },
  ],
  image: [
    {
      validator: (_, value: string, callback) => {
        if ((formData.styles.backgroundType === 'image' && formData.image) || formData.tempImage) {
          callback()
        } else {
          callback(new Error('This field is required'))
        }
      },
      trigger: 'submit',
    },
  ],
})

const isDirty = ref(false)
const isImageUploading = ref(false)

watch(
  [() => formData.advertiserList, () => formData.categories, () => formData.isAllAdvertisersSelected],
  async () => {
    await ruleFormRef.value?.validateField('advertiserList')
  },
  { deep: true },
)

const submitForm = (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  return formEl.validate((valid) => {
    if (valid) {
      isDirty.value = false
      emit('handleSubmit', formData)
    }
  })
}

watch(formData, () => previewData?.update(normalizeReturnAdFormToPreview(formData)), { immediate: true })
onMounted(() => previewData?.update(normalizeReturnAdFormToPreview(formData)))
</script>

<template>
  <div class="form-container">
    <el-form
      ref="ruleFormRef"
      :model="formData"
      :rules="rules"
      hide-required-asterisk
      :validate-on-rule-change="false"
      :scroll-to-error="true"
      label-position="top"
      class="campaign-form__content"
      @change="isDirty = true"
    >
      <FlexComponent direction="column" :gap="48">
        <Card title="Target">
          <ReturnAdFormTarget
            v-model:advertiser-group="formData.advertiserGroup"
            v-model:advertiser-list="formData.advertiserList"
            v-model:internal-name="formData.internalName"
            v-model:return-link="formData.returnLink"
            v-model:categories="formData.categories"
            v-model:is-all-advertisers-selected="formData.isAllAdvertisersSelected"
            v-model:advertiser="formData.advertiser"
            :is-edit-mode="isEditMode"
          />
        </Card>

        <Card title="Customization">
          <ReturnAdFormCustomization
            v-model:text="formData.styles.text"
            v-model:background-type="formData.styles.backgroundType"
            v-model:background-color="formData.styles.backgroundColor"
            v-model:text-color="formData.styles.textColor"
            v-model:text-position="formData.styles.textPosition"
            v-model:button-background-color="formData.styles.buttonBackgroundColor"
            v-model:button-text-color="formData.styles.buttonTextColor"
            v-model:temp-image="formData.tempImage"
            v-model:is-image-uploading="isImageUploading"
            :image="formData.image"
          />
        </Card>

        <Card title="Start/End">
          <ReturnAdFormCalendar
            v-model:has-starts-now="formData.hasStartsNow"
            v-model:has-open-end="formData.hasOpenEnd"
            v-model:starts-at="formData.startsAt"
            v-model:ends-at="formData.endsAt"
            v-model:view-limit="formData.viewLimit"
            v-model:click-limit="formData.clickLimit"
          />
        </Card>

        <NoteComponent> Please note: Changes take up to 1 minute to appear live after saving. </NoteComponent>

        <BoxComponent :mb="48">
          <FlexComponent :gap="8" justify="end" flex="1">
            <ButtonComponent variant="secondary" :disabled="isLoading || isImageUploading" @click="emit('handleBack')">
              Cancel
            </ButtonComponent>
            <ButtonComponent
              :is-loading="isLoading"
              :is-disabled="isLoading || isImageUploading"
              data-test="submitFormBtn"
              @click.prevent="() => submitForm(ruleFormRef)"
            >
              {{ isEditMode ? 'Save' : 'Create' }} return ad
            </ButtonComponent>
          </FlexComponent>
        </BoxComponent>
      </FlexComponent>
    </el-form>
  </div>
</template>

<style scoped lang="scss">
.form-container {
  max-width: 640px;
}
</style>
